import { LoadingIndicator } from "@sunrun/experience-ui-components";
import { useFlags, useFlagsmith, useFlagsmithLoading } from "flagsmith/react";
import { SimplifiedOfferReviewPage } from "components/pages/SimplifiedOfferReviewPage";
import { ReviewPage } from "components/pages/ReviewPage";
import { FeatureFlags } from "fixtures/features";
import { useReviewOfferForRouting } from "hooks/useReviewOfferForRouting";
import { getOfferReviewSimplifiedFlagInputs } from "utils/getOfferReviewSimplifiedFlagInputs";
import { evaluateOfferReviewSimplifiedExperience } from "utils/shouldShowSimplifiedOfferReviewExperience";
import { isSimplifiedPresentationFlagsReady } from "utils/isSimplifiedPresentationFlagsReady";
import type { SelfSupplyEligibleMarket } from "utils/selfSupplyEligibility";

type OfferReviewSimplifiedGateProps = {
  presentationTargets: SelfSupplyEligibleMarket[];
  createdDateCutoff: string | undefined;
};

const OfferReviewSimplifiedGate = ({
  presentationTargets,
  createdDateCutoff,
}: OfferReviewSimplifiedGateProps) => {
  const { offer, offerCreatedAt, isLoading } = useReviewOfferForRouting();

  if (isLoading) {
    return <LoadingIndicator color="black" fullScreen />;
  }

  const summary = offer?.financial?.summary;
  const decision = evaluateOfferReviewSimplifiedExperience({
    offerReviewSimplifiedEnabled: true,
    presentationTargets,
    createdDateCutoff,
    offerCreatedAt,
    state: summary?.state,
    utility: summary?.utility,
    offer,
  });

  if (decision.show) {
    return <SimplifiedOfferReviewPage prefetchedOffer={offer} />;
  }

  return <ReviewPage />;
};

/**
 * Offer review entry: feature flag + offer type choose between two full page implementations.
 * Keep standard and simplified pages separate; do not share layout branches between them.
 */
const OfferReviewRoute = () => {
  const flagsmith = useFlagsmith();
  const flagsmithLoading = useFlagsmithLoading();
  const flags = useFlags([FeatureFlags.OFFER_REVIEW_SIMPLIFIED]);

  if (!isSimplifiedPresentationFlagsReady(flagsmith, flagsmithLoading)) {
    return <LoadingIndicator color="black" fullScreen />;
  }

  const {
    offerReviewSimplifiedEnabled,
    presentationTargets,
    createdDateCutoff,
  } = getOfferReviewSimplifiedFlagInputs(flagsmith, flags);

  if (!offerReviewSimplifiedEnabled) {
    return <ReviewPage />;
  }

  return (
    <OfferReviewSimplifiedGate
      presentationTargets={presentationTargets}
      createdDateCutoff={createdDateCutoff}
    />
  );
};

export { OfferReviewRoute };
